import { useIsEditor } from 'modules/archive';
import { useProjectAccessStatus } from 'modules/auth';
import { useHumanReadable } from 'modules/data';
import { EventContentField } from 'modules/events';
import { ContentField } from 'modules/forms';
import { useI18n } from 'modules/i18n';
import { useProject } from 'modules/routes';
import { Spinner } from 'modules/spinners';
import PropTypes from 'prop-types';

import Biography from './Biography';
import NameOrPseudonym from './NameOrPseudonym';
import getDisplayedMetadataFields from './getDisplayedMetadataFields';
import { usePersonWithAssociations } from './hooks';

export default function PersonData({ interview, intervieweeId }) {
    const { t, locale } = useI18n();
    const { humanReadable } = useHumanReadable();
    const { project } = useProject();
    const { projectAccessGranted } = useProjectAccessStatus(project);
    const isEditor = useIsEditor();

    const {
        data: person,
        isLoading,
        isValidating,
    } = usePersonWithAssociations(intervieweeId);

    const displayedMetadataFields = getDisplayedMetadataFields(
        Object.values(project.metadata_fields),
        projectAccessGranted
    );

    if (isLoading) {
        return <Spinner />;
    }

    if (intervieweeId === null || !person) {
        return <div>{t('modules.person.no_interviewee')}</div>;
    }

    return (
        <>
            <NameOrPseudonym
                type="name"
                person={person}
                interview={interview}
                fetching={isValidating}
            />
            {displayedMetadataFields.map((field, index) => {
                if (field.source === 'EventType') {
                    const events = person.events?.filter(
                        (event) => event.event_type_id === field.event_type_id
                    );

                    if (!events || events.length === 0) {
                        return null;
                    }

                    return (
                        <EventContentField
                            key={index}
                            label={field.label?.[locale]}
                            events={events}
                        />
                    );
                }

                if (field.name === 'pseudonym_or_name') {
                    return (
                        <NameOrPseudonym
                            key={index}
                            type="pseudonym"
                            person={person}
                            interview={interview}
                            fetching={isValidating}
                        />
                    );
                }

                const label = field.label?.[locale] || t(field.name);
                const value = humanReadable({
                    obj: person,
                    attribute: field.name,
                });

                if ((value === null || value === '---') && !isEditor) {
                    return null;
                }

                return (
                    <ContentField
                        key={field.name}
                        label={label}
                        value={value}
                        linkUrls={field.name === 'description'}
                    />
                );
            })}

            {person && <Biography />}
        </>
    );
}

PersonData.propTypes = {
    interview: PropTypes.object.isRequired,
    intervieweeId: PropTypes.number,
};
